import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import type { CompetitorStat } from '../api/types';
import { count, percent } from '../utils/format';

import { ChartTooltip } from './ChartTooltip';
import { Legend } from './Legend';
import { CHROME, DIVERGE } from './palette';

/**
 * Per shop, the split of shared products into "they undercut us" and "we hold
 * the price". That is a composition inside each shop's overlap, so it is a
 * stacked bar; the two segments use the diverging poles because they are the
 * bad and good outcome of the same contest, and a 2px gap keeps them apart.
 */
export function CompetitorChart({ stats }: { stats: CompetitorStat[] }) {
  const rows = stats
    .map((stat) => ({
      shopName: stat.shopName,
      cheaperCount: Number(stat.cheaperCount),
      weHold: Number(stat.productCount) - Number(stat.cheaperCount),
      productCount: Number(stat.productCount),
      avgDiffPct: stat.avgDiffPct,
      avgRatingPercent: stat.avgRatingPercent,
    }))
    .sort((left, right) => right.cheaperCount - left.cheaperCount)
    .slice(0, 12);

  if (rows.length === 0) {
    return <div className="state">No competitor offers captured yet.</div>;
  }

  return (
    <>
      <ResponsiveContainer width="100%" height={Math.max(200, rows.length * 32 + 40)}>
        <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 20, bottom: 4, left: 4 }} barCategoryGap={5}>
          <CartesianGrid stroke={CHROME.grid} horizontal={false} />
          <XAxis
            type="number"
            tick={{ fill: CHROME.inkMuted, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
          />
          <YAxis
            type="category"
            dataKey="shopName"
            tick={{ fill: CHROME.inkSecondary, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={160}
          />
          <Tooltip
            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) {
                return null;
              }

              const row = payload[0].payload as (typeof rows)[number];

              return (
                <ChartTooltip
                  title={row.shopName}
                  rows={[
                    {
                      key: 'cheaper',
                      label: 'They undercut us',
                      value: count(row.cheaperCount),
                      color: DIVERGE.high,
                    },
                    { key: 'hold', label: 'We hold', value: count(row.weHold), color: DIVERGE.low },
                    { key: 'shared', label: 'Shared products', value: count(row.productCount) },
                    { key: 'diff', label: 'Mean price diff', value: percent(row.avgDiffPct) },
                    {
                      key: 'rating',
                      label: 'Heureka rating',
                      value: row.avgRatingPercent === null ? '—' : `${row.avgRatingPercent}%`,
                    },
                  ]}
                />
              );
            }}
          />
          <Bar isAnimationActive={false} dataKey="cheaperCount" stackId="a" fill={DIVERGE.high} maxBarSize={22} radius={[4, 0, 0, 4]} />
          <Bar isAnimationActive={false} dataKey="weHold" stackId="a" fill={DIVERGE.low} maxBarSize={22} radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>

      <Legend
        entries={[
          { label: 'They undercut us', color: DIVERGE.high },
          { label: 'We hold the price', color: DIVERGE.low },
        ]}
      />
    </>
  );
}
